import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { expertEvaluate } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Circle, CircleCheck, CircleAlert, CircleX, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { newProjectId } from "@/lib/storage";

const EDITION_LABELS = {
  express: "Express+",
  professional: "Professional+",
  expert: "Expert",
  corporate: "Corporate",
};
const EDITIONS = ["express", "professional", "expert", "corporate"];

const STATUS = {
  green: { color: "text-emerald-500", bg: "bg-emerald-500/15", border: "border-emerald-500/40", Icon: CircleCheck, label: "Geeignet" },
  yellow: { color: "text-amber-500", bg: "bg-amber-500/15", border: "border-amber-500/40", Icon: CircleAlert, label: "Eingeschränkt" },
  red: { color: "text-rose-500", bg: "bg-rose-500/15", border: "border-rose-500/40", Icon: CircleX, label: "Nicht verfügbar" },
};

const FEATURE_STATUS_ICON = {
  included: { Icon: CircleCheck, className: "text-emerald-500" },
  optional: { Icon: CircleAlert, className: "text-amber-500" },
  unavailable: { Icon: CircleX, className: "text-rose-500" },
};

export default function ExpertMode({ features, loading, onOpenInConfigurator }) {
  const [selected, setSelected] = useState({});
  const [evalResult, setEvalResult] = useState(null);

  const handleOpenInConfigurator = (ed) => {
    if (!onOpenInConfigurator) return;
    onOpenInConfigurator({
      id: newProjectId(),
      name: "",
      mode: "configurator",
      source: "expert",
      sourceEditionLabel: `XProtect ${EDITION_LABELS[ed]}`,
      configuration: { mode: "configurator", edition: ed, quantities: { [`${ed}:base`]: 1 } },
      code: "",
    });
  };

  const selectedKeys = useMemo(() => Object.entries(selected).filter(([, v]) => v).map(([k]) => k), [selected]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await expertEvaluate(selectedKeys);
        if (!cancelled) setEvalResult(r);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedKeys]);

  if (loading || !features) {
    return <div className="p-12 text-center text-muted-foreground">Lädt …</div>;
  }

  // Only status-type features are selectable (value-type like maxDevices are not toggleable)
  const groups = features.featureGroups.map((g) => ({
    ...g,
    features: g.features.filter((f) => f.type === "status"),
  })).filter((g) => g.features.length > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
      <Card className="p-5 md:p-6 border-border bg-card card-elev">
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Expertenmodus — Funktionen direkt wählen</h2>
          <p className="text-sm text-muted-foreground">Wählen Sie die benötigten Funktionen. Rechts sehen Sie live, welche Edition wie geeignet ist.</p>
        </div>
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.key}>
              <h3 className="text-sm uppercase tracking-wider text-primary font-semibold mb-2">{g.label}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {g.features.map((f) => {
                  const checked = !!selected[f.key];
                  return (
                    <Label
                      key={f.key}
                      htmlFor={`fx-${f.key}`}
                      data-testid={`expert-feature-${f.key}`}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 cursor-pointer tile-hover",
                        checked ? "border-primary bg-primary/10" : "border-border bg-secondary/30 hover:bg-secondary/50"
                      )}
                    >
                      <Checkbox
                        id={`fx-${f.key}`}
                        checked={checked}
                        onCheckedChange={(v) => setSelected((s) => ({ ...s, [f.key]: !!v }))}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{f.label}</div>
                        <div className="grid grid-cols-4 gap-1 mt-2 text-[10px]">
                          {EDITIONS.map((e) => {
                            const status = f.values[e] || "unavailable";
                            const meta = FEATURE_STATUS_ICON[status] || FEATURE_STATUS_ICON.unavailable;
                            const Icn = meta.Icon;
                            return (
                              <div key={e} className="flex items-center gap-1">
                                <Icn className={cn("w-3 h-3", meta.className)} />
                                <span className="text-muted-foreground truncate">{EDITION_LABELS[e]}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </Label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Live edition status */}
      <div className="space-y-4">
        <Card className="p-4 border-border bg-card card-elev sticky top-24">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Editionen — Live</h3>
          <div className="space-y-3">
            {EDITIONS.map((ed) => {
              const info = evalResult?.editions?.[ed] || { status: "green", score: 100, issues: [] };
              const s = STATUS[info.status] || STATUS.green;
              const Icn = s.Icon;
              return (
                <div
                  key={ed}
                  data-testid={`expert-edition-${ed}`}
                  className={cn("rounded-lg border p-3", s.border, s.bg)}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Icn className={cn("w-4 h-4", s.color)} />
                      <span className="font-semibold text-sm">{EDITION_LABELS[ed]}</span>
                    </div>
                    <span className="text-sm font-bold tabular-nums">{info.score}%</span>
                  </div>
                  <Progress value={info.score} className="h-1.5" />
                  <div className="text-[11px] text-muted-foreground mt-1.5">{s.label}</div>
                  {info.issues && info.issues.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-[11px]">
                      {info.issues.slice(0, 3).map((iss, i) => (
                        <li key={i} className="text-muted-foreground truncate">• {iss}</li>
                      ))}
                      {info.issues.length > 3 && (
                        <li className="text-muted-foreground italic">+ {info.issues.length - 3} weitere</li>
                      )}
                    </ul>
                  )}
                  {onOpenInConfigurator && info.status !== "red" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid={`btn-expert-configure-${ed}`}
                      onClick={() => handleOpenInConfigurator(ed)}
                      className="mt-2 h-7 px-2 text-[11px] text-primary hover:bg-primary/10 -ml-2"
                    >
                      <SlidersHorizontal className="w-3 h-3 mr-1" /> Im Konfigurator zusammenstellen
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          {selectedKeys.length === 0 && (
            <p className="text-xs text-muted-foreground mt-4">
              Noch keine Funktion gewählt — alle Editionen wären geeignet.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
